import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import {
  FeatureCard,
  FeatureCardHeader,
} from "@/components/ui/feature-shell";
import { DataPanelTitle } from "@/components/ui/data-panel";
import { fmtDate } from "@/components/athlete/formatters";
import type { StatsPayload } from "@/routes/compare";

type Profile = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
};

type Props = {
  statsA: StatsPayload;
  statsB: StatsPayload;
  profileA: Profile;
  profileB: Profile;
};

type MetricDef = {
  label: string;
  getA: () => number | null;
  getB: () => number | null;
  format: (v: number | null) => string;
  lowerIsBetter?: boolean;
};

function fmtPct(v: number | null): string {
  if (v == null) return "—";
  return `${v.toFixed(1)}%`;
}

function fmtVal(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString();
}

function fmtRank(v: number | null): string {
  if (v == null) return "—";
  return `#${v}`;
}

function winner(
  a: number | null,
  b: number | null,
  lowerIsBetter: boolean,
): "a" | "b" | null {
  if (a == null || b == null) return null;
  if (a === b) return null;
  if (lowerIsBetter) return a < b ? "a" : "b";
  return a > b ? "a" : "b";
}

export function StatsComparison({ statsA, statsB, profileA, profileB }: Props) {
  const metrics: MetricDef[] = useMemo(
    () => [
      {
        label: "Total Races",
        getA: () => statsA.summary.totalRaces,
        getB: () => statsB.summary.totalRaces,
        format: fmtVal,
      },
      {
        label: "Finisher Rate",
        getA: () =>
          statsA.summary.totalRaces > 0
            ? (statsA.summary.finisherCount / statsA.summary.totalRaces) * 100
            : null,
        getB: () =>
          statsB.summary.totalRaces > 0
            ? (statsB.summary.finisherCount / statsB.summary.totalRaces) * 100
            : null,
        format: fmtPct,
      },
      {
        label: "Best Overall Rank",
        getA: () => statsA.performance.bestOverallRank,
        getB: () => statsB.performance.bestOverallRank,
        format: fmtRank,
        lowerIsBetter: true,
      },
      {
        label: "Avg Overall Rank",
        getA: () => statsA.performance.avgOverallRank,
        getB: () => statsB.performance.avgOverallRank,
        format: fmtRank,
        lowerIsBetter: true,
      },
      {
        label: "Best AG Rank",
        getA: () => statsA.competitive.bestAgeGroupRank,
        getB: () => statsB.competitive.bestAgeGroupRank,
        format: fmtRank,
        lowerIsBetter: true,
      },
      {
        label: "Avg AG Rank",
        getA: () => statsA.competitive.avgAgeGroupRank,
        getB: () => statsB.competitive.avgAgeGroupRank,
        format: fmtRank,
        lowerIsBetter: true,
      },
      {
        label: "Top 10 Finishes",
        getA: () => statsA.performance.top10Count,
        getB: () => statsB.performance.top10Count,
        format: fmtVal,
      },
      {
        label: "Total Points",
        getA: () => statsA.summary.totalPoints,
        getB: () => statsB.summary.totalPoints,
        format: fmtVal,
      },
    ],
    [statsA, statsB],
  );

  // Personal bests: only race types both athletes have
  const sharedPBs = useMemo(() => {
    const aByType = new Map<string, (typeof statsA.personalBests)[0]>();
    for (const pb of statsA.personalBests) {
      if (pb.rank === 1) aByType.set(pb.raceType, pb);
    }
    const bByType = new Map<string, (typeof statsB.personalBests)[0]>();
    for (const pb of statsB.personalBests) {
      if (pb.rank === 1) bByType.set(pb.raceType, pb);
    }
    const shared: Array<{
      raceType: string;
      a: (typeof statsA.personalBests)[0];
      b: (typeof statsB.personalBests)[0];
    }> = [];
    for (const [type, aPB] of aByType) {
      const bPB = bByType.get(type);
      if (bPB) shared.push({ raceType: type, a: aPB, b: bPB });
    }
    return shared.sort((x, y) => x.raceType.localeCompare(y.raceType));
  }, [statsA.personalBests, statsB.personalBests]);

  // Race type breakdown: union of both athletes' types
  const breakdownRows = useMemo(() => {
    const aMap = new Map(statsA.breakdownByRaceType.map((b) => [b.raceType, b]));
    const bMap = new Map(statsB.breakdownByRaceType.map((b) => [b.raceType, b]));
    const allTypes = new Set([...aMap.keys(), ...bMap.keys()]);
    return [...allTypes]
      .map((type) => ({
        raceType: type,
        a: aMap.get(type) ?? null,
        b: bMap.get(type) ?? null,
        shared: aMap.has(type) && bMap.has(type),
      }))
      .sort((x, y) => {
        // Shared types first, then by race count
        if (x.shared !== y.shared) return x.shared ? -1 : 1;
        const aTotal = (x.a?.races ?? 0) + (x.b?.races ?? 0);
        const bTotal = (y.a?.races ?? 0) + (y.b?.races ?? 0);
        return bTotal - aTotal;
      });
  }, [statsA.breakdownByRaceType, statsB.breakdownByRaceType]);

  function formatRaceType(rt: string): string {
    return rt
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return (
    <div className="compare-stats-section">
      {/* Overview Metrics */}
      <FeatureCard>
        <FeatureCardHeader>
          <DataPanelTitle>Performance Comparison</DataPanelTitle>
        </FeatureCardHeader>
        <div className="compare-stats-grid">
          <div className="compare-stats-header">
            <span className="compare-stats-header-name compare-stats-header-name--a">
              {profileA.lastName}
            </span>
            <span />
            <span className="compare-stats-header-name compare-stats-header-name--b">
              {profileB.lastName}
            </span>
          </div>
          {metrics.map((m) => {
            const aVal = m.getA();
            const bVal = m.getB();
            const w = winner(aVal, bVal, m.lowerIsBetter ?? false);
            return (
              <div className="compare-stats-row" key={m.label}>
                <span
                  className={`compare-stats-value compare-stats-value--a${w === "a" ? " compare-stats-value--winner" : ""}`}
                >
                  {m.format(aVal)}
                </span>
                <span className="compare-stats-label">{m.label}</span>
                <span
                  className={`compare-stats-value compare-stats-value--b${w === "b" ? " compare-stats-value--winner" : ""}`}
                >
                  {m.format(bVal)}
                </span>
              </div>
            );
          })}
        </div>
      </FeatureCard>

      {/* Personal Bests */}
      {sharedPBs.length > 0 && (
        <FeatureCard>
          <FeatureCardHeader>
            <DataPanelTitle>Personal Bests</DataPanelTitle>
            <p className="feature-card-copy">
              Fastest times in shared race types.
            </p>
          </FeatureCardHeader>
          <div className="compare-pb-section">
            {sharedPBs.map(({ raceType, a, b }) => {
              const faster =
                a.finishTimeSeconds < b.finishTimeSeconds ? "a" : b.finishTimeSeconds < a.finishTimeSeconds ? "b" : null;
              return (
                <div className="compare-pb-row" key={raceType}>
                  <div
                    className={`compare-pb-value compare-pb-value--a${faster === "a" ? " compare-pb-value--winner" : ""}`}
                  >
                    <span className="compare-pb-time">{a.finishTimeFormatted}</span>
                    <span className="compare-pb-event">
                      <Link
                        to="/events/$eventId"
                        params={{ eventId: a.eventId }}
                        search={{ ageGroup: undefined, highlight: undefined, highlightRank: undefined, page: undefined }}
                        className="compare-pb-link"
                      >
                        {a.eventName}
                      </Link>
                      {a.eventDate && (
                        <span className="compare-pb-date">{fmtDate(a.eventDate)}</span>
                      )}
                    </span>
                  </div>
                  <span className="compare-pb-type">{formatRaceType(raceType)}</span>
                  <div
                    className={`compare-pb-value compare-pb-value--b${faster === "b" ? " compare-pb-value--winner" : ""}`}
                  >
                    <span className="compare-pb-time">{b.finishTimeFormatted}</span>
                    <span className="compare-pb-event">
                      <Link
                        to="/events/$eventId"
                        params={{ eventId: b.eventId }}
                        search={{ ageGroup: undefined, highlight: undefined, highlightRank: undefined, page: undefined }}
                        className="compare-pb-link"
                      >
                        {b.eventName}
                      </Link>
                      {b.eventDate && (
                        <span className="compare-pb-date">{fmtDate(b.eventDate)}</span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </FeatureCard>
      )}

      {/* Race Type Breakdown */}
      {breakdownRows.length > 0 && (
        <FeatureCard>
          <FeatureCardHeader>
            <DataPanelTitle>Race Type Breakdown</DataPanelTitle>
            <p className="feature-card-copy">
              Races and best age-group rank by distance.
            </p>
          </FeatureCardHeader>
          <div className="compare-table-wrap">
            <table className="compare-breakdown-table">
              <thead>
                <tr>
                  <th className="compare-breakdown-th--a">Races</th>
                  <th className="compare-breakdown-th--a">Best AG</th>
                  <th>Race Type</th>
                  <th className="compare-breakdown-th--b">Races</th>
                  <th className="compare-breakdown-th--b">Best AG</th>
                </tr>
              </thead>
              <tbody>
                {breakdownRows.map(({ raceType, a, b, shared }) => (
                  <tr
                    key={raceType}
                    className={shared ? "" : "compare-breakdown-row--partial"}
                  >
                    <td className="compare-breakdown-val compare-breakdown-val--a">
                      {a?.races ?? "—"}
                    </td>
                    <td className="compare-breakdown-val compare-breakdown-val--a">
                      {a?.bestAgeGroupRank != null ? `#${a.bestAgeGroupRank}` : "—"}
                    </td>
                    <td className="compare-breakdown-type">{formatRaceType(raceType)}</td>
                    <td className="compare-breakdown-val compare-breakdown-val--b">
                      {b?.races ?? "—"}
                    </td>
                    <td className="compare-breakdown-val compare-breakdown-val--b">
                      {b?.bestAgeGroupRank != null ? `#${b.bestAgeGroupRank}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FeatureCard>
      )}
    </div>
  );
}
